/**
 * Typed transport error. Frontends switch on `code` (a `DomainErrorCode`), never on
 * the human-readable `message`. Thrown by the client when the GraphQL response carries
 * `errors[]` with an `extensions.code`.
 */
import type { DomainErrorCode } from "@mb-group/shared-types";
import { DOMAIN_ERROR_CODES } from "@mb-group/shared-types";

/** A single field-level validation error (path + human-readable message). */
export interface FieldError {
	path: string;
	message: string;
}

/**
 * Typed transport error thrown when a GraphQL response carries `errors[]`. Frontends
 * branch on {@link GraphQLDomainError.code}, never on the message.
 */
export class GraphQLDomainError extends Error {
	readonly code: DomainErrorCode;
	readonly correlationId?: string;
	readonly fields?: FieldError[];

	/**
	 * @param code Machine-readable domain error code.
	 * @param message Human-readable message (for logs/diagnostics only).
	 * @param options Optional correlation id and field-level errors.
	 */
	constructor(
		code: DomainErrorCode,
		message: string,
		options: { correlationId?: string; fields?: FieldError[] } = {},
	) {
		super(message);
		this.name = "GraphQLDomainError";
		this.code = code;
		if (options.correlationId !== undefined) this.correlationId = options.correlationId;
		if (options.fields !== undefined) this.fields = options.fields;
	}
}

const CODES = new Set<string>(DOMAIN_ERROR_CODES);

/** Type guard: is `value` a known {@link DomainErrorCode}? */
export function isDomainErrorCode(value: unknown): value is DomainErrorCode {
	return typeof value === "string" && CODES.has(value);
}
